import type { ListCache } from '@jackedlogs/shared';

// web-rnw twin of ./asyncListCache.ts (see that file's header) — same
// namespace + JSON shape, localStorage instead of AsyncStorage. Resolved
// instead of the bare file by Vite's `.web.ts` extension preference.

type Options = {
  version?: number;
};

const namespace = (cacheKey: string, version: number) =>
  `jl.asyncList.${cacheKey}.v${version}`;

export function asyncStorageListCache<T>(
  cacheKey: string,
  opts: Options = {},
): ListCache<T> {
  const storageKey = namespace(cacheKey, opts.version ?? 1);
  return {
    read: async () => {
      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { items?: unknown } | null;
        if (!parsed || !Array.isArray(parsed.items)) return null;
        return parsed.items as T[];
      } catch {
        return null; // private mode / corrupt entry — treat as empty
      }
    },
    write: async (items) => {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ cachedAt: new Date().toISOString(), items }),
        );
      } catch {
        // quota exceeded / private mode — non-fatal, network still drives.
      }
    },
  };
}

export type ResourceCache<T> = {
  read: () => Promise<T | null>;
  write: (value: T) => Promise<void>;
};

export function asyncStorageResourceCache<T>(
  cacheKey: string,
  opts: Options = {},
): ResourceCache<T> {
  const storageKey = namespace(cacheKey, opts.version ?? 1);
  return {
    read: async () => {
      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { value?: unknown } | null;
        if (!parsed || parsed.value === undefined) return null;
        return parsed.value as T;
      } catch {
        return null;
      }
    },
    write: async (value) => {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ cachedAt: new Date().toISOString(), value }),
        );
      } catch {
        // quota exceeded / private mode — non-fatal.
      }
    },
  };
}
